import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Import Modular Components
import Layout, { Branding } from './game/Layout';
import LoginCode from './game/LoginCode';
import LoginEmail from './game/LoginEmail';
import Registration from './game/Registration';
import ActiveGame from './game/ActiveGame';
import FutureEvents from './game/FutureEvents';
import ProfileEdit from './game/ProfileEdit';
import EventRegistration from './game/EventRegistration';
import ScratchMatches from './game/ScratchMatches';
import MyMatches from './game/MyMatches';
import Chat from './game/Chat';

export default function GameView() {
    // --- States ---
    const [participant, setParticipant] = useState(null);
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [isViewingAllMatches, setIsViewingAllMatches] = useState(false);
    const [activeChat, setActiveChat] = useState(null);
    const activeChatRef = React.useRef(activeChat);
    useEffect(() => { activeChatRef.current = activeChat; }, [activeChat]);

    const [unreadCount, setUnreadCount] = useState(0);
    const [allMatches, setAllMatches] = useState([]);
    const saveTokenToIDB = (token) => {
        const request = indexedDB.open("SolterosDB", 1);
        request.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains("config")) {
                db.createObjectStore("config");
            }
        };
        request.onsuccess = (e) => {
            const db = e.target.result;
            const transaction = db.transaction("config", "readwrite");
            transaction.objectStore("config").put(token, "token");
        };
    };

    function urlBase64ToUint8Array(base64String) {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    const subscribeToPush = async (activeToken) => {
        if (!('serviceWorker' in navigator)) return;

        if (Notification.permission === 'denied') {
            console.warn('Push Notifications denied by user');
            return;
        }
        
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array('BIvSOBLOCF8BjwgWTCqD5iKtXurMmDB0JcDPO0qZVoMc4YtiZ5tfvuvn1622xYhJ41_TFnX2iI5eghl21W-dLi8')
            });
            
            await axios.post('/speeddate/api/subscribe', {
                token: activeToken,
                subscription: subscription
            });
            console.log('Push Subscribed');
        } catch (err) {
            if (err.name === 'NotAllowedError') {
                console.warn('Push Notifications permission denied during subscribe');
            } else {
                console.error('Push Subscribe Error:', err);
            }
        }
    };
    const [token, setToken] = useState('');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [loginMode, setLoginMode] = useState('token'); // 'token' or 'email'
    const [participations, setParticipations] = useState([]);
    const [needsRegistration, setNeedsRegistration] = useState(false);
    const [futureEvents, setFutureEvents] = useState([]);    const [others, setOthers] = useState([]);
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [noteDrafts, setNoteDrafts] = useState({});
    const [savingStatus, setSavingStatus] = useState({});
    const [isCheckingToken, setIsCheckingToken] = useState(false);
    const [selectedEventForReg, setSelectedEventForReg] = useState(null);
    const [registerEventId, setRegisterEventId] = useState(null);
    const [allParticipations, setAllParticipations] = useState([]);
    const [revealingMatchesFor, setRevealingMatchesFor] = useState(null);
    const [isForcedEmail, setIsForcedEmail] = useState(false);
    
    const timeouts = React.useRef({});
    const autoRefreshInterval = React.useRef(null);

    // --- Auto-open event registration from URL param ---
    useEffect(() => {
        if (registerEventId && futureEvents.length > 0 && !selectedEventForReg) {
            const targetEvent = futureEvents.find(e => String(e.id) === String(registerEventId));
            if (targetEvent) {
                setSelectedEventForReg(targetEvent);
            }
        }
    }, [registerEventId, futureEvents, selectedEventForReg]);

    // --- Lifecycle ---
    useEffect(() => {
        const path = window.location.pathname;
        if (path.includes('/login')) {
            setLoginMode('email');
            setIsForcedEmail(true);
        }

        const params = new URLSearchParams(window.location.search);
        const urlToken = params.get('token') || params.get('TOKEN');
        const urlEventId = params.get('eventId');
        const savedToken = localStorage.getItem('participant_token');
        const savedTime = localStorage.getItem('participant_token_time');

        // Store eventId from URL for auto-registration after login
        if (urlEventId) {
            setRegisterEventId(urlEventId);
            setLoginMode('email');
            setIsForcedEmail(true);
            // Pre-fetch future events so we can find the target event after login
            fetchFutureEvents();
        }
        
        let activeToken = urlToken;
        if (!activeToken && savedToken && savedTime) {
            // Ripristiniamo a 24 ore come richiesto
            if (new Date().getTime() - parseInt(savedTime) < 24 * 60 * 60 * 1000) activeToken = savedToken;
            else { localStorage.removeItem('participant_token'); localStorage.removeItem('participant_token_time'); }
        }

        if (activeToken) {
            setIsCheckingToken(true);
            handleVerify(activeToken).finally(() => {
                setIsCheckingToken(false);
                subscribeToPush(activeToken);
            });
        }

        // Request notification permission
        if ("Notification" in window) {
            Notification.requestPermission();
        }
        // Unread messages polling
        const checkUnread = async () => {
            const currentToken = localStorage.getItem('participant_token');
            if (!currentToken) return;
            try {
                const res = await axios.get(`/speeddate/api/messages/unread?token=${currentToken.toUpperCase()}`);
                const count = res.data.count;
                
                setUnreadCount(prevCount => {
                    if (count > 0 && count > prevCount) {
                        // New message notification
                        const latest = res.data.latest;
                        // Use a ref for activeChat to avoid closure issues
                        if (latest && activeChatRef.current?.userId !== latest.senderId) {
                            setTimeout(() => {
                                if ("Notification" in window && Notification.permission === "granted") {
                                    new Notification("Solteros: Nuovo Messaggio!", {
                                        body: "Hai ricevuto un nuovo messaggio da un tuo match.",
                                        icon: "/speeddate/logo192.png"
                                    });
                                }
                                if (window.Swal) {
                                    window.Swal.fire({
                                        title: 'Nuovo Messaggio!',
                                        text: `Hai ricevuto un nuovo messaggio da un tuo match.`,
                                        icon: 'info',
                                        toast: true,
                                        position: 'top-end',
                                        showConfirmButton: false,
                                        timer: 3000
                                    });
                                }
                            }, 0);
                        }
                    }
                    return count;
                });
            } catch (err) {}
        };

        checkUnread(); // Esegui subito al caricamento
        const unreadInterval = setInterval(checkUnread, 10000);

        return () => {
            if (autoRefreshInterval.current) clearInterval(autoRefreshInterval.current);
            clearInterval(unreadInterval);
        };
    }, []);

    useEffect(() => {
        if (!participant || !participant.id || !participant.EventId) return;
        
        const silentRefresh = async () => {
            try {
                const [pRes, othersRes] = await Promise.all([
                    axios.get(`/speeddate/api/users/verify?token=${participant.token.toUpperCase()}`),
                    participant.Event?.status >= 3 ? axios.get(`/speeddate/api/participants/others?token=${participant.token.toUpperCase()}`) : Promise.resolve(null)
                ]);
                const p = pRes.data.success ? pRes.data.participant : pRes.data;
                setParticipant(p);
                if (othersRes) setOthers(othersRes.data);
            } catch (err) {}
        };

        if (autoRefreshInterval.current) clearInterval(autoRefreshInterval.current);
        autoRefreshInterval.current = setInterval(silentRefresh, 20000);
        
        if (participant.MatchEmailSent && !results) fetchResults(participant.token);
        
        return () => clearInterval(autoRefreshInterval.current);
    }, [participant?.id, participant?.EventId]);

    // --- Actions ---
    const handleVerify = async (providedToken) => {
        const t = providedToken || token;
        if (!t) return;
        setLoading(true);
        try {
            const res = await axios.get(`/speeddate/api/users/verify?token=${t.toUpperCase()}`);
            if (res.data.isNewUser) setIsEditingProfile(true);
            const p = res.data.success ? res.data.participant : res.data;
            
            if (p && p.id) {
                // If this is a User object, it might have many participations. 
                // Let's find one that is currently active (status 3 or 4)
                let activeP = p;
                const parts = p.Participations || p.participations || [];
                const active = parts.find(pp => pp.Event && pp.Event.status >= 3 && pp.Event.status < 5);
                if (active) {
                    activeP = { ...active, token: p.token || active.token };
                }

                setParticipant(activeP);
                setToken(t.toUpperCase());
                saveTokenToIDB(t.toUpperCase());
                localStorage.setItem('participant_token', t.toUpperCase());
                localStorage.setItem('participant_token_time', new Date().getTime().toString());
                
                if (activeP.EventId) {
                    if (activeP.Event?.status >= 3 && activeP.Event?.status < 5) fetchOthers(activeP.token);
                    if (activeP.MatchEmailSent) fetchResults(activeP.token);
                }
                fetchFutureEvents();

                // Also fetch all past participations (works for both Participant and User tokens)
                try {
                    const pastRes = await axios.get(`/speeddate/api/users/past-participations?token=${t.toUpperCase()}`);
                    if (pastRes.data.participations?.length > 0) {
                        setAllParticipations(pastRes.data.participations);
                    }
                } catch (_) {}

                return activeP;
            } else {
                handleLogout();
            }
        } catch (err) {
            if (err.response && (err.response.status === 401 || err.response.status === 404)) {
                handleLogout();
            }
            if (!providedToken) {
                window.Swal.fire({
                    title: 'Accesso Fallito',
                    text: 'Controlla il codice inserito o la connessione.',
                    icon: 'error',
                });
            }
        } finally { setLoading(false); }
    };

    const handleLogout = () => {
        localStorage.removeItem('participant_token');
        localStorage.removeItem('participant_token_time');
        setParticipant(null);
        setToken('');
        setParticipations([]);
        setNeedsRegistration(false);
        setIsEditingProfile(false);
        setEmail('');
        setOtpSent(false);
    };

    const fetchFutureEvents = async () => {
        try {
            const currentToken = token || localStorage.getItem('participant_token');
            const res = await axios.get('/speeddate/api/events/future', {
                headers: { 'x-user-token': currentToken }
            });
            setFutureEvents(res.data);
        } catch (err) { console.error(err); }
    };

    const fetchResults = async (t) => {
        try {
            const res = await axios.get(`/speeddate/api/participants/results?token=${t.toUpperCase()}`);
            setResults(res.data);
        } catch (err) {}
    };

    const fetchOthers = async (t) => {
        try {
            const res = await axios.get(`/speeddate/api/participants/others?token=${t.toUpperCase()}`);
            setOthers(res.data);
        } catch (err) {}
    };

    const fetchAllMatches = async () => {
        setLoading(true);
        try {
            const currentToken = token || localStorage.getItem('participant_token');
            const res = await axios.get(`/speeddate/api/users/all-matches?token=${currentToken.toUpperCase()}`);
            const data = Array.isArray(res.data) ? res.data : [];
            setAllMatches(data);
            setIsViewingAllMatches(true);
        } catch (err) {
            console.error(err);
            setAllMatches([]);
            window.Swal.fire({
                title: 'Errore',
                text: 'Impossibile recuperare i match. Riprova più tardi.',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleVote = async (targetId, choice, isMagic) => {
        if (!participant) return;
        // Optimistic update
        setOthers(prev => prev.map(o => o.id === targetId ? { ...o, ReceivedVotes: [{ choice, isMagic }] } : o));
        
        try {
            await axios.post('/speeddate/api/participants/vote', {
                token: participant.token,
                targetId,
                choice,
                isMagic,
                note: noteDrafts[targetId] || ''
            });
        } catch (err) {
            window.Swal.fire({ title: 'Errore', text: 'Voto non salvato. Riprova.', icon: 'error' });
        }
    };

    const handleNoteChange = (otherId, note) => {
        setNoteDrafts(prev => ({ ...prev, [otherId]: note }));
        if (timeouts.current[otherId]) clearTimeout(timeouts.current[otherId]);
        
        timeouts.current[otherId] = setTimeout(async () => {
            const currentOther = others.find(o => o.id === otherId);
            const vote = currentOther?.ReceivedVotes?.[0];
            if (vote) {
                try {
                    await axios.post('/speeddate/api/participants/vote', {
                        token: participant.token,
                        targetId: otherId,
                        choice: vote.choice,
                        isMagic: vote.isMagic,
                        note
                    });
                } catch (err) {}
            }
        }, 1000);
    };

    // --- Login Handlers ---
    const handleLoginEmail = async (emailAddr) => {
        setLoading(true);
        try {
            const res = await axios.post('/speeddate/api/users/request-otp', { email: emailAddr });
            if (res.data.success) {
                setEmail(emailAddr);
                setOtpSent(true);
            }
        } catch (err) {
            window.Swal.fire({ title: 'Errore', text: 'Email non trovata o errore server.', icon: 'error' });
        } finally { setLoading(false); }
    };

    const handleVerifyOTP = async (code) => {
        setLoading(true);
        try {
            const res = await axios.post('/speeddate/api/users/verify-otp', { email, otp: code });
            if (res.data.success) {
                const parts = res.data.participations || [];
                const user = res.data.user;

                // Store all participations for the PastEvents component
                setAllParticipations(parts);

                // Find an active/ongoing game participation (status 3 or 4)
                const activeP = parts.find(p => p.Event && p.Event.status >= 3 && p.Event.status < 5);
                if (activeP) {
                    // There is an active game - go straight to it using participant token
                    handleVerify(activeP.token);
                } else if (user?.token) {
                    // No active game - use the USER token so /users/verify returns the full user
                    // with all participations attached, which feeds PastEvents
                    handleVerify(user.token);
                } else if (parts.length > 0) {
                    handleVerify(parts[0].token);
                } else if (res.data.needsRegistration) {
                    setNeedsRegistration(true);
                }
            }
        } catch (err) {
            window.Swal.fire({ title: 'Errore', text: 'Codice non valido o scaduto.', icon: 'error' });
        } finally { setLoading(false); }
    };

    const handleCompleteRegistration = async (formData) => {
        setLoading(true);
        try {
            const finalEmail = email || participant?.email || formData.email;
            const res = await axios.post('/speeddate/api/users/complete-registration', { ...formData, email: finalEmail });
            if (res.data.success) {
                setParticipant(res.data.participant);
                setNeedsRegistration(false);
                setToken(res.data.participant.token);
                localStorage.setItem('participant_token', res.data.participant.token);
                localStorage.setItem('participant_token_time', new Date().getTime().toString());
            }
        } catch (err) {
            window.Swal.fire({ title: 'Errore', text: 'Impossibile completare la registrazione.', icon: 'error' });
        } finally { setLoading(false); }
    };
    const handleBellClick = async () => {
        try {
            const res = await axios.get(`/speeddate/api/messages/unread?token=${token.toUpperCase()}`);
            if (res.data.latest) {
                // Find the match in allMatches or fetch it
                const matchesRes = await axios.get(`/speeddate/api/users/all-matches?token=${token.toUpperCase()}`);
                const matches = matchesRes.data;
                const matchObj = matches.find(m => m.match.userId === res.data.latest.senderId);
                if (matchObj) {
                    setActiveChat(matchObj.match);
                    setIsViewingAllMatches(false);
                } else {
                    // Fallback to matches list
                    setIsViewingAllMatches(true);
                }
            } else {
                setIsViewingAllMatches(true);
            }
        } catch (err) {
            setIsViewingAllMatches(true);
        }
    };

    // --- View Routing ---
    // 1. If we have a participant, check if profile is complete
    if (participant) {
        if (!participant.firstName) {
            return (
                <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
                    <Registration 
                        onComplete={handleCompleteRegistration} 
                        initialEmail={email || participant.email} 
                        initialData={participant}
                        loading={loading}
                    />
                </Layout>
            );
        }

        if (isEditingProfile) return (
            <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
                <ProfileEdit 
                    participant={participant} 
                    token={token}
                    onUpdate={(updated) => { setParticipant(updated); setIsEditingProfile(false); }} 
                    onCancel={() => setIsEditingProfile(false)} 
                />
            </Layout>
        );

        if (revealingMatchesFor) return (
            <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
                <ScratchMatches 
                    participation={revealingMatchesFor}
                    onBack={() => setRevealingMatchesFor(null)}
                />
            </Layout>
        );

        if (activeChat) return (
            <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
                <Chat 
                    token={token} 
                    otherUser={activeChat} 
                    onBack={() => setActiveChat(null)} 
                />
            </Layout>
        );

        if (isViewingAllMatches) return (
            <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
                <MyMatches 
                    matches={allMatches}
                    onBack={() => setIsViewingAllMatches(false)}
                    onChat={(match) => { setActiveChat(match); setIsViewingAllMatches(false); }}
                />
            </Layout>
        );

        if (selectedEventForReg) return (
            <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
                <EventRegistration 
                    event={selectedEventForReg} 
                    participant={participant}
                    onBack={() => {
                        setSelectedEventForReg(null);
                        setRegisterEventId(null);
                        // Clean eventId from URL when going back
                        const params = new URLSearchParams(window.location.search);
                        params.delete('eventId');
                        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
                        window.history.replaceState({}, '', newUrl);
                    }}
                    onRegistered={(updatedP) => {
                        setParticipant(updatedP);
                        setSelectedEventForReg(null);
                        setRegisterEventId(null);
                        const params = new URLSearchParams(window.location.search);
                        params.delete('eventId');
                        const newUrl = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
                        window.history.replaceState({}, '', newUrl);
                    }}
                />
            </Layout>
        );

        const isGameActive = participant.Event?.status >= 3 && participant.Event?.status < 5;
        const isEventToday = participant.Event?.date && new Date(participant.Event.date).toDateString() === new Date().toDateString();

        // Merge all sources of participations to be sure we show everything
        const mergedParts = [
            ...(participant.Participations || []),
            ...(participant.participations || []),
            ...(allParticipations || [])
        ];
        // Unique by id
        const pastParticipations = Array.from(new Map(mergedParts.map(p => [p.id, p])).values());

        if (!participant.EventId || (!isGameActive && !isEventToday)) return (
            <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
                <FutureEvents 
                    participant={participant} 
                    futureEvents={futureEvents} 
                    handleLogout={handleLogout} 
                    onEditProfile={() => setIsEditingProfile(true)}
                    onSelectEvent={(ev) => setSelectedEventForReg(ev)}
                    pastParticipations={pastParticipations}
                    onRevealMatches={(p) => setRevealingMatchesFor(p)}
                    onShowMatches={fetchAllMatches}
                />
            </Layout>
        );

        return (
            <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
                <ActiveGame 
                    participant={participant}
                    others={others}
                    results={results}
                    loading={loading}
                    noteDrafts={noteDrafts}
                    handleNoteChange={handleNoteChange}
                    handleVote={handleVote}
                    handleVerify={handleVerify}
                    handleLogout={handleLogout}
                    onEditProfile={() => setIsEditingProfile(true)}
                    onShowMatches={fetchAllMatches}
                />
            </Layout>
        );
    }

    // 2. No participant - Auth Flow
    return (
        <Layout unreadCount={unreadCount} onBellClick={participant ? handleBellClick : null}>
            {needsRegistration ? (
                <Registration 
                    onComplete={handleCompleteRegistration} 
                    initialEmail={email} 
                    loading={loading}
                />
            ) : participations.length > 1 ? (
                <div className="card glass-premium border-0 p-5 text-center shadow-lg fade-in-up" style={{ borderRadius: '45px' }}>
                    <Branding />
                    <h2 className="fw-900 text-dark mt-4 mb-4">Scegli il tuo Evento</h2>
                    <div className="list-group list-group-flush gap-3">
                        {participations.map(p => (
                            <button key={p.id} className="list-group-item list-group-item-action border-0 rounded-4 p-4 shadow-sm text-start transition-all hover-scale" onClick={() => handleVerify(p.token)}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="fw-900 mb-1">{p.Event?.name}</h5>
                                        <small className="text-muted fw-600"><i className="fas fa-calendar-alt me-2"></i>{new Date(p.Event?.date).toLocaleDateString()}</small>
                                    </div>
                                    <i className="fas fa-chevron-right text-pink-600"></i>
                                </div>
                            </button>
                        ))}
                    </div>
                    <button className="btn btn-link mt-4 text-muted fw-700" onClick={handleLogout}>Torna al Login</button>
                </div>
            ) : (
                <div className="fade-in">
                    {loginMode === 'token' ? (
                        <LoginCode 
                            token={token} 
                            setToken={setToken} 
                            loading={loading} 
                            onVerify={() => handleVerify(token)} 
                            loginMode={loginMode} 
                            setLoginMode={setLoginMode} 
                            setOtpSent={setOtpSent} 
                            isForcedEmail={isForcedEmail}
                        />
                    ) : (
                        <LoginEmail 
                            email={email} 
                            setEmail={setEmail} 
                            otp={otp}
                            setOtp={setOtp}
                            loading={loading} 
                            otpSent={otpSent} 
                            setOtpSent={setOtpSent} 
                            handleRequestOTP={handleLoginEmail} 
                            handleVerifyOTP={handleVerifyOTP} 
                            loginMode={loginMode} 
                            setLoginMode={setLoginMode} 
                            isForcedEmail={isForcedEmail}
                        />
                    )}
                </div>
            )}
        </Layout>
    );
}
